import { useForm } from 'react-hook-form';
import { useBreakpointValue, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  CompanyCreationActivity,
  CompanyCreationSasu,
} from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form/';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import OBStickyButton from '../../../../components/OBStickyButton/OBStickyButton';
import formSchema, { FormSchemaValid } from './formSchema';
import locales from './locales';

type Activity = Pick<CompanyCreationActivity, 'legalName'>;

interface ObjetSocialReviewProps {
  objetSocial?: CompanyCreationSasu['objetSocial'];
  onSubmit: (values: FormSchemaValid) => void;
  activities: [Activity, ...Activity[]]; // we need at least one activity
}

const ObjetSocialReview = asBentoPage<ObjetSocialReviewProps>(
  ({ activities, Layout, objetSocial, onPrev, onSubmit }) => {
    const isMobile = useBreakpointValue({ base: true, md: false }) as boolean;
    const { formState, handleSubmit, register } = useForm({
      defaultValues: {
        objetSocial: objetSocial ?? '',
      },
      resolver: zodResolver(formSchema),
    });

    const [mainActivity, ...secondaryActivities] = activities;
    const secondaryActivitiesText = secondaryActivities
      .map(({ legalName }) => legalName)
      .join(', ');

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />} variant="larger">
        <Header marginBottom="space-32" title={locales.title} />

        <SunshineCard.Group marginBottom="space-32">
          <SunshineCard title={mainActivity.legalName}>
            <SunshineCard.Slot name="action">
              <SunshineTag colorScheme="grey" size="sm">
                {locales.mainActivity}
              </SunshineTag>
            </SunshineCard.Slot>
          </SunshineCard>
          {secondaryActivitiesText.length ? (
            <SunshineCard title={secondaryActivitiesText}>
              <SunshineCard.Slot name="action">
                <SunshineTag colorScheme="blue" size="sm">
                  {locales.secondaryActivities}
                </SunshineTag>
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
        </SunshineCard.Group>

        <Text marginBottom="space-16">{locales.explanation}</Text>
        <VStack
          align="stretch"
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          spacing="space-32"
        >
          <Form.Field error={formState.errors.objetSocial?.message}>
            <Form.Textarea
              paddingX="space-20"
              paddingY="space-16"
              rows={8}
              {...register('objetSocial')}
            />
          </Form.Field>
          <Callout hideIcon variant="info">
            <Callout.Content>{locales.calloutText}</Callout.Content>
          </Callout>
          <OBStickyButton
            isExpanded={isMobile}
            justifyContent={{ base: 'center', md: 'left' }}
            type="submit"
          >
            {locales.cta}
          </OBStickyButton>
        </VStack>
      </Layout>
    );
  },
);

export default ObjetSocialReview;
